import { useQuery } from "@apollo/client";

import Box from "ds/components/Box";
import PageInfoHeader from "ds/components/PageInfoHeader";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import useTypedFlags from "hooks/useTypedFlags";
import { AccountContext } from "views/AccountWrapper";
import useErrorHandle from "hooks/useErrorHandle";
import PageLoading from "components/loading/PageLoading";
import { FederationProvider } from "types/generated";

import ExternalAccountsGitHubCard from "./GitHubCard";
import ExternalAccountsSlackCard from "./SlackCard";
import ExternalAccountsTeamsCard from "./TeamsCard";
import { GET_PERSONAL_CONNECTIONS } from "./gql";

const ExternalAccounts = () => {
  const { accountName } = useTypedContext(AccountContext);
  const { githubLink, slackLink, microsoftTeamsLink } = useTypedFlags();
  useTitle(`Personal Settings · Connections · ${accountName}`);

  const { data, loading, error } = useQuery(GET_PERSONAL_CONNECTIONS);

  const federations = data?.viewer?.identity?.federations ?? [];
  const githubFederation = federations.find(
    (federation) => federation.provider === FederationProvider.GithubOauth
  );
  const slackFederation = federations.find(
    (federation) => federation.provider === FederationProvider.SlackOauth
  );
  const teamsFederation = federations.find(
    (federation) => federation.provider === FederationProvider.MicrosoftTeamsOauth
  );

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading && !data) {
    return <PageLoading />;
  }

  return (
    <>
      <PageInfoHeader
        title="External accounts"
        caption="Connect your external accounts to get notifications and attribute your activity across tools."
      />

      {data && (
        <Box direction="column" gap="large" padding="x-large" limitWidth="medium">
          {githubLink && <ExternalAccountsGitHubCard federation={githubFederation} />}
          {slackLink && <ExternalAccountsSlackCard federation={slackFederation} />}
          {microsoftTeamsLink && <ExternalAccountsTeamsCard federation={teamsFederation} />}
        </Box>
      )}
    </>
  );
};

export default ExternalAccounts;
